import React, { useEffect, useMemo, useState } from 'react'
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Grid2 } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MetricCard from '../../metrics/MetricCard'
import PerformanceChart from '../../charts/PerformanceChart'
import { useAuth } from '../../../context/AuthContext'
import { getObjectsWithFilter } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'

const stylePaid = (data) => (data > 0 ? { color: 'green' } : undefined)
const styleUnpaid = (data) => (data <= 0.1 ? { color: 'green' } : { color: 'red' })

const baseMetricsDefinition = [
  { title: 'Nb factures émises', field: 'totalItems', object: 'invoices' },
  { title: 'Facturation (CA émis)', field: 'totalAmount', type: 'currency', object: 'invoices' },
  {
    title: 'Encaissements (Trésorerie)',
    field: 'totalAmount',
    object: 'incomes',
    type: 'currency',
    styleCondition: stylePaid
  },
  {
    title: 'Reste à recouvrer',
    field: 'totalAmount',
    type: 'currency',
    object: 'invoices',
    filter: { status: { $ne: '80' } },
    styleCondition: styleUnpaid
  }
]

const MetricCardsList = ({ metrics, suffix = '' }) => (
  <Grid2 container spacing={2} sx={{ width: '100%' }}>
    {metrics.map((metric) => (
      <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={metric.title + suffix}>
        <MetricCard
          title={metric.title}
          object={metric.object}
          field={metric.field}
          type={metric.type}
          filter={metric.filter}
          styleCondition={metric.styleCondition}
        />
      </Grid2>
    ))}
  </Grid2>
)

const Dashboard = () => {
  const [chartData, setChartData] = useState([])
  const { user } = useAuth()
  const { lastPeriod, currentPeriod } = useMemo(() => {
    // Helper pour formater en YYYYMM (ex: 202502)
    const formatPeriod = (date) => {
      const y = date.getUTCFullYear()
      const m = (date.getUTCMonth() + 1).toString().padStart(2, '0')
      return `${y}${m}`
    }

    const now = new Date()

    // Période précédente
    const lastDate = new Date()
    lastDate.setUTCMonth(lastDate.getUTCMonth() - 1)

    return {
      lastPeriod: formatPeriod(lastDate),
      currentPeriod: formatPeriod(now)
    }
  }, [])

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const res = await getObjectsWithFilter('stats/revenue-flow', {
          company_id: user.company_id
        })
        setChartData(res.data)
      } catch {
        toast.error('Erreur lors du chargement', { autoClose: 2000 })
      }
    }
    fetchChartData()
  }, [])

  const baseMetrics = useMemo(() => baseMetricsDefinition, [])

  const periodsWithValues = useMemo(
    () => [
      { label: 'période précédente', period: lastPeriod },
      { label: 'période courante', period: currentPeriod }
    ],
    [lastPeriod, currentPeriod]
  )

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 65px)', overflowY: 'auto' }}>
      {/* SECTION GRAPHIQUE */}
      <Accordion sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>Analyse des Flux Financiers (6 derniers mois)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PerformanceChart data={chartData} />
        </AccordionDetails>
      </Accordion>

      {/* SECTION VUE GLOBALE */}
      <Accordion sx={{ mb: 2, borderRadius: '8px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>Vue Globale (Historique complet)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <MetricCardsList metrics={baseMetricsDefinition} />
        </AccordionDetails>
      </Accordion>
      {/* Sections Temporelles */}
      {periodsWithValues.map(({ label, period }) => {
        const metricsWithPeriod = baseMetricsDefinition.map((metric) => ({
          ...metric,
          title: metric.title,
          // On applique le filtre sur 'periodYearMonth' qui est commun aux deux objets
          filter: { ...metric.filter, periodYearMonth: period }
        }))

        return (
          <Accordion key={period} sx={{ mb: 2, borderRadius: '8px !important' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='h6' sx={{ textTransform: 'capitalize' }}>
                Détails : {label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexFlow: 'row wrap', gap: 2 }}>
                <MetricCardsList metrics={metricsWithPeriod} suffix={period} />
              </Box>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </Box>
  )
}

export default Dashboard
